// https://boost.allnetru.pro/api/dev/dicts/routes?prefix=api/partner/events/{event}/creatives
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreativeAdItem,
  CreativeEditorDataItem,
  CreativeGenerateSubtitlesParams,
  CreativeVideoFramesParams,
  GetPartnerCreativeFontsResponse,
  GetPartnerCreativesResponse,
  GetPartnerCreativeTemplate,
  GetPartnerCreativesTypesResponse,
  GetTlaCreativesResponse,
  GetTlaPriceResponse,
  GetTlaSlotsResponse,
  PartnerCreativeFontDescriptor,
  PostTlaCreativeRequestBody,
  PatchTlaCreativeRequestBody,
  PostTlaCreativeResponse,
} from './models';
import { CampaignEditorResponseItem } from '../campaigns/models';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
const partner_creatives = `partner_creatives`;

export const partnerCreatives = createApi({
  reducerPath: partner_creatives,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partner_creatives],
  endpoints: (build) => ({
    // Common creatives list with filters (for re-use)
    getPartnerCreativesIndex: build.query<
      GetPartnerCreativesResponse,
      {
        filters?: {
          ids?: string[];
          search?: string;
          statuses?: string[];
          campaigns?: string[];
          types?: string[];
          events?: string[];
          page?: number;
        };
      }
    >({
      query: ({ filters }) => {
        const params = new URLSearchParams();
        if (filters?.search) {
          params.append('filters[search]', filters.search);
        }
        if (filters?.ids?.length) {
          filters.ids.forEach((id) => params.append('filters[ids][]', id));
        }
        if (filters?.statuses?.length) {
          filters.statuses.forEach((status) => params.append('filters[statuses][]', status));
        }
        if (filters?.campaigns?.length) {
          filters.campaigns.forEach((id) => params.append('filters[campaigns][]', id));
        }
        if (filters?.types?.length) {
          filters.types.forEach((t) => params.append('filters[types][]', t));
        }
        if (filters?.events?.length) {
          filters.events.forEach((e) => params.append('filters[events][]', e));
        }
        if (filters?.page) {
          params.append('page', String(filters.page));
        }
        const queryString = params.toString();
        return {
          url: `/partner/creatives${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: partner_creatives, id: 'INDEX' }],
    }),
    getPartnerCreatives: build.query<GetPartnerCreativesResponse, { campaignId: string }>({
      query: ({ campaignId }) => {
        return {
          url: `/partner/campaigns/${campaignId}/creatives`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: (_result, _error, { campaignId }) => [
        { type: partner_creatives, id: campaignId },
      ],
    }),
    // Copy a creative to a group
    postCopyPartnerCreative: build.mutation<
      { data: CreativeAdItem },
      { creativeId: string; campaignGroupId: string; campaignId: string; linkWithSource?: boolean }
    >({
      query: ({ creativeId, campaignGroupId, linkWithSource = false }) => {
        return {
          method: 'POST',
          url: `/partner/creatives/${creativeId}/copy`,
          headers: {
            Accept: 'application/json',
          },
          body: {
            campaign_group_id: campaignGroupId,
            link_with_source: linkWithSource,
          },
        };
      },
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: partner_creatives, id: campaignId },
        { type: partner_creatives, id: 'INDEX' },
      ],
    }),
    postCreateCreativeByCampaignId: build.mutation<
      { data: CampaignEditorResponseItem },
      { campaignId: string; groupId: string; data: CreativeEditorDataItem }
    >({
      query: ({ campaignId, groupId, data }) => {
        return {
          method: 'POST',
          url: `partner/campaigns/${campaignId}/groups/${groupId}/creatives`,
          headers: {
            Accept: 'application/json',
          },
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: partner_creatives, id: campaignId },
        { type: partner_creatives, id: 'INDEX' },
      ],
    }),
    patchCreativeDataById: build.mutation<
      { data: CampaignEditorResponseItem },
      { campaignId: string; creativeId: string; data: CreativeEditorDataItem }
    >({
      query: ({ campaignId, creativeId, data }) => ({
        body: data,
        method: 'PATCH',
        url: `/partner/campaigns/${campaignId}/creatives/${creativeId}`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: partner_creatives, id: campaignId },
        { type: partner_creatives, id: 'INDEX' },
      ],
    }),
    getPartnerCreativesTemplates: build.query<
      GetPartnerCreativeTemplate,
      { eventId: string; type: number | string; isUpload?: boolean }
    >({
      query: ({ eventId, type, isUpload }) => {
        return {
          url: `/partner/creatives/templates/${type}`,
          params: { event_id: eventId, ...(isUpload ? { is_upload: 1 } : {}) },
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
    }),
    patchPartnerCampaignsCreativeStatusById: build.mutation<
      { data: CreativeAdItem[] },
      {
        campaignId: string;
        status: number;
        transition: number;
        filters: {
          ids: string[];
        };
      }
    >({
      query: ({ campaignId, status, transition, filters }) => {
        return {
          method: 'PATCH',
          url: `/partner/campaigns/${campaignId}/creatives/status/${status}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
          body: {
            transition,
            status,
            filters: {
              ids: filters?.ids,
            },
          },
        };
      },
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: partner_creatives, id: campaignId },
        { type: partner_creatives, id: 'INDEX' },
      ],
    }),
    postCreateCreativeGenerateSubtitles: build.mutation<
      { data: CampaignEditorResponseItem },
      CreativeGenerateSubtitlesParams
    >({
      query: (data) => {
        return {
          method: 'POST',
          url: `partner/creatives/subtitles`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
          body: data,
        };
      },
    }),
    postCreateCreativeVideoFrames: build.mutation<
      { data: CampaignEditorResponseItem },
      CreativeVideoFramesParams
    >({
      query: (data) => {
        return {
          method: 'POST',
          url: `partner/creatives/frames`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
          body: data,
        };
      },
    }),
    postCreativeUpload: build.mutation<{ file: BinaryType }, { file: Blob }>({
      query: ({ file }) => {
        console.log('file postCreativeUpload', file);
        return {
          method: 'POST',
          url: `partner/creatives/upload`,
          headers: {
            Accept: 'application/json', // Specify JSON response
            'Content-Type': 'multipart/form-data',
          },
          body: {
            file,
          },
        };
      },
    }),
    postTlaCreative: build.mutation<PostTlaCreativeResponse, PostTlaCreativeRequestBody>({
      query: (body) => ({
        method: 'POST',
        url: '/partner/creatives/tla',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      }),
      // a placement changes the list and the month's counters, nothing else that
      // happens to carry the creatives tag
      invalidatesTags: [
        { type: partner_creatives, id: 'TLA_LIST' },
        { type: partner_creatives, id: 'TLA_SLOTS' },
      ],
    }),
    getTlaPrice: build.query<GetTlaPriceResponse, { eventId: string | number }>({
      query: ({ eventId }) => ({
        url: `/partner/creatives/tla/price/${eventId}`,
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    patchTlaCreative: build.mutation<PostTlaCreativeResponse, PatchTlaCreativeRequestBody>({
      query: ({ creativeId, ...body }) => ({
        url: `/partner/creatives/tla/${creativeId}`,
        method: 'PATCH',
        body,
        headers: { Accept: 'application/json' },
      }),
      // only the placement list and the month's counters, not every query holding
      // the creatives tag
      invalidatesTags: [
        { type: partner_creatives, id: 'TLA_LIST' },
        { type: partner_creatives, id: 'TLA_SLOTS' },
      ],
    }),
    getTlaCreatives: build.query<GetTlaCreativesResponse, { eventId?: string | number }>({
      query: ({ eventId } = {}) => ({
        url: '/partner/creatives/tla',
        params: eventId ? { event_id: eventId } : undefined,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [{ type: partner_creatives, id: 'TLA_LIST' }],
    }),
    getTlaSlots: build.query<
      GetTlaSlotsResponse,
      { eventId: string | number; month?: string; channel?: number }
    >({
      query: ({ eventId, month, channel }) => ({
        url: `/partner/creatives/tla/slots/${eventId}`,
        // slots are counted per channel: each channel has its own monthly limit
        params: { ...(month ? { month } : {}), ...(channel ? { channel } : {}) },
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [{ type: partner_creatives, id: 'TLA_SLOTS' }],
    }),
    getPartnerCreativesTypes: build.query<
      GetPartnerCreativesTypesResponse,
      { eventId?: string | number; campaignGroupId?: string | number }
    >({
      query: ({ eventId, campaignGroupId } = {}) => {
        const params: Record<string, string | number> = {};
        if (eventId) params.event_id = eventId;
        if (campaignGroupId) params.campaign_group_id = campaignGroupId;
        return {
          url: `/partner/creatives/types`,
          params: Object.keys(params).length > 0 ? params : undefined,
        };
      },
    }),
    patchRenameCreative: build.mutation<
      { data: CreativeAdItem },
      { campaignId: string; creativeId: string; title: string }
    >({
      query: ({ campaignId, creativeId, title }) => ({
        method: 'PATCH',
        url: `/partner/campaigns/${campaignId}/creatives/${creativeId}/rename`,
        headers: { Accept: 'application/json' },
        body: { title },
      }),
      invalidatesTags: (_result, _error, { campaignId }) => [
        { type: partner_creatives, id: campaignId },
        { type: partner_creatives, id: 'INDEX' },
      ],
    }),
    getPartnerCreativeFonts: build.query<GetPartnerCreativeFontsResponse, void>({
      query: () => ({
        url: `/partner/creatives/fonts`,
        headers: {
          Accept: 'application/json',
        },
      }),
      transformResponse: (response: GetPartnerCreativeFontsResponse) => {
        const safeFonts = Array.isArray(response?.data)
          ? response.data.filter((font): font is PartnerCreativeFontDescriptor =>
              Boolean(font?.name && font?.file),
            )
          : [];
        return {
          data: safeFonts,
        };
      },
    }),
  }),
});

export const {
  useGetPartnerCreativesIndexQuery,
  useGetPartnerCreativesQuery,
  useGetPartnerCreativesTemplatesQuery,
  useLazyGetPartnerCreativesTemplatesQuery,
  usePostCreativeUploadMutation,
  usePostCreateCreativeByCampaignIdMutation,
  usePatchCreativeDataByIdMutation,
  usePatchPartnerCampaignsCreativeStatusByIdMutation,
  usePostCreateCreativeGenerateSubtitlesMutation,
  usePostCreateCreativeVideoFramesMutation,
  useGetPartnerCreativesTypesQuery,
  useGetPartnerCreativeFontsQuery,
  usePostCopyPartnerCreativeMutation,
  usePatchRenameCreativeMutation,
  usePostTlaCreativeMutation,
  usePatchTlaCreativeMutation,
  useGetTlaPriceQuery,
  useGetTlaCreativesQuery,
  useGetTlaSlotsQuery,
} = partnerCreatives;
